import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, AlertTriangle, Link2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fmtNum, downloadBlob, filenameFromHeaders, errorMsg } from '@/lib/recuentos';

const ProductList = ({ title, items, tone, testId, extra }) => (
  <div className={`rounded-lg border p-3 ${tone}`} data-testid={testId}>
    <div className="flex items-center justify-between gap-2 mb-2">
      <p className="text-sm font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {title} ({items.length})</p>
      {extra}
    </div>
    <ul className="max-h-36 overflow-y-auto text-xs space-y-0.5">
      {items.map((p) => (
        <li key={p.codigo} className="flex justify-between gap-2">
          <span><span className="font-mono text-slate-500">{p.codigo}</span> {p.nombre}</span>
          <span className="font-semibold whitespace-nowrap">{fmtNum(p.stock)} {p.unidad}</span>
        </li>
      ))}
    </ul>
  </div>
);

const IngredientesTable = ({ rows }) => (
  <div className="max-h-56 overflow-y-auto border rounded-lg">
    <table className="w-full text-xs" data-testid="stock-justo-ingredientes-table">
      <thead className="bg-slate-50 text-slate-600 sticky top-0">
        <tr><th className="px-3 py-2 text-left">ID</th><th className="px-3 py-2 text-left">Ingrediente Justo</th><th className="px-3 py-2 text-right">Cantidad</th></tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id_justo} className="border-t" title={r.origen.join(' + ')}>
            <td className="px-3 py-1.5 font-mono text-slate-500">{r.id_justo}</td>
            <td className="px-3 py-1.5">{r.nombre_justo}</td>
            <td className="px-3 py-1.5 text-right font-semibold">{fmtNum(r.cantidad)} <span className="text-slate-400 font-normal">{r.unidad}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const StockJustoExportDialog = ({ warehouseId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get('/recuentos/stock-tupu/preview', { params: { warehouse_id: warehouseId } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(errorMsg(e, 'Error al generar vista previa')); onOpenChange(false); });
  }, [open, warehouseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const problemas = data ? data.sin_equivalencia.length + data.negativos.length : 0;

  const descargar = async (conAviso) => {
    if (conAviso && problemas > 0 && !window.confirm(
      `Hay ${data.sin_equivalencia.length} productos sin equivalencia y ${data.negativos.length} con stock negativo. No se incluirán en el archivo. ¿Descargar igual?`)) return;
    setDownloading(true);
    try {
      const res = await api.post('/recuentos/stock-tupu/exportar', { warehouse_id: warehouseId }, { responseType: 'blob' });
      downloadBlob(res.data, filenameFromHeaders(res.headers, 'recuento_GreciaCentral_stockTUPU.xlsx'));
      toast.success(`Recuento exportado: ${data.total_ingredientes} ingredientes. Guardado en el historial de Recuentos.`);
      onOpenChange(false);
    } catch (e) {
      toast.error('No se pudo exportar el recuento');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="stock-justo-dialog">
        <DialogHeader>
          <DialogTitle>Exportar recuento para Justo</DialogTitle>
          <DialogDescription>Stock actual de TUPU como inventario total de Grecia – Bodega Central.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-orange-50 border border-orange-200 p-3">
              <p className="text-sm text-slate-600">Ingredientes que se exportan</p>
              <p className="text-3xl font-bold text-orange-600" data-testid="stock-justo-total">{data.total_ingredientes}</p>
            </div>
            <IngredientesTable rows={data.ingredientes} />
            {data.sin_equivalencia.length > 0 && (
              <ProductList title="Con stock pero sin equivalencia Justo (no se exportan)" items={data.sin_equivalencia}
                tone="bg-amber-50 border-amber-200 text-amber-900" testId="stock-justo-sin-equivalencia"
                extra={<Link to="/admin/justo?tab=equivalencias" className="text-xs font-semibold underline flex items-center gap-1" data-testid="stock-justo-link-equivalencias"><Link2 className="w-3 h-3" /> Completar equivalencias</Link>} />
            )}
            {data.negativos.length > 0 && (
              <ProductList title="Con stock negativo (no se exportan hasta corregirlos)" items={data.negativos}
                tone="bg-red-50 border-red-200 text-red-900" testId="stock-justo-negativos" />
            )}
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button variant="outline" disabled={downloading || !data.total_ingredientes} onClick={() => descargar(false)} data-testid="stock-justo-descargar-validos-btn">
                Descargar solo lo válido
              </Button>
              <Button className="bg-orange-500 hover:bg-orange-600" disabled={downloading || !data.total_ingredientes} onClick={() => descargar(true)} data-testid="stock-justo-descargar-btn">
                {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Descargar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
